import type { GetStaticProps } from "next"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import { getCategoryTeasers } from "@/components/resources/data/markdown/posts"
import {
  type Category,
  getAllCategories,
  getAllLatestEntriesWithNewsletter,
  type Teaser,
} from "@/components/resources/data/sanity/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { CategoryArchive } from "@/components/resources/templates/CategoryArchive"

interface Props {
  entries: Teaser[]
  categories: Category[]
}

const title = "Data-driven\n research on crypto"
const lead = "Market & project research, video interviews and our weekly newsletter"
const Resources = ({ categories, entries }: Props) => {
  return (
    <>
      <PageMeta
        meta={{
          title: `${title.replace(/\n/, "")}`,
          description: lead,
        }}
      />
      <CategoryArchive entries={entries} title={title} categories={categories} lead={lead} />
    </>
  )
}

Resources.getLayout = SiteLayout

export default Resources

export const getStaticProps: GetStaticProps<Props> = async () => {
  const entriesP = getAllLatestEntriesWithNewsletter()
  const catsP = getAllCategories()
  const [sanityEntries, categories] = await Promise.all([entriesP, catsP])

  // Include markdown-based engineering posts in the "All" view
  const mdEngineeringEntries = await getCategoryTeasers("engineering")
  // Sanity posts that are already exported to Markdown render from the file
  const mdSlugs = new Set(mdEngineeringEntries.map(({ slug }) => slug))
  const entries = [
    ...sanityEntries.filter(
      ({ category, slug }) => category !== "engineering" || !mdSlugs.has(slug),
    ),
    ...mdEngineeringEntries,
  ].sort((a, b) => {
    const bt = new Date(b.publishedAt).getTime()
    const at = new Date(a.publishedAt).getTime()
    if (Number.isNaN(at)) return 1
    if (Number.isNaN(bt)) return -1
    return bt - at
  })

  return {
    props: {
      entries,
      categories,
    },
    revalidate: 180, // TODO: temporary to allow content editing without an hour wait for build
  }
}
